import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { DataTable } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import { AuthContext } from '../App';
import type { Project, Task, Client } from '../types';
import { ArrowLeft, FolderKanban, ListChecks, Users, Wallet, Plus, Trash2, Pencil, CalendarDays, ShieldCheck, Lock } from 'lucide-react';

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext)!;
  const { canWrite } = usePermissions({ pageKey: 'projects' });
  const isAdmin = user?.globalRole === 'ADMIN';

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editBillingMethod, setEditBillingMethod] = useState('PROJECT');
  const [clients, setClients] = useState<Client[]>([]);
  const [editClientId, setEditClientId] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskName, setTaskName] = useState('');
  const [taskBillable, setTaskBillable] = useState(true);
  const [taskBusy, setTaskBusy] = useState(false);
  const [usersList, setUsersList] = useState<{ id: string; name: string }[]>([]);
  const [memberUserId, setMemberUserId] = useState('');
  const [memberRole, setMemberRole] = useState<'MEMBER' | 'PM'>('MEMBER');
  const [memberBusy, setMemberBusy] = useState(false);

  const [projectPermissions, setProjectPermissions] = useState<any[]>([]);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);
  const [modifiedPerms, setModifiedPerms] = useState<Set<string>>(new Set());

  const PAGE_KEYS = [
    'dashboard', 'projects', 'clients', 'teams', 'time-entries', 'timesheets',
    'planned-hours', 'budgets', 'reports', 'exports', 'templates', 'admin',
  ];
  const ROLES = ['ADMIN', 'PM', 'USER'];

  async function fetchProject() {
    if (!id) return;
    setLoading(true); setError('');
    try {
      const p = await api<Project>(`/projects/${id}`);
      setProject(p);
      setTasks(p.tasks || []);
    } catch (err: any) { setError(err.message); }
    setLoading(false);
  }

  useEffect(() => { fetchProject(); }, [id]);
  useEffect(() => { async function f() { try { const d = await api<{ id: string; name: string }[]>('/users'); setUsersList(d || []); } catch (err: any) { setError(err.message || 'Failed to load users'); } } f(); }, []);
  useEffect(() => { async function f() { try { const d = await api<Client[]>('/clients'); setClients(d || []); } catch (err: any) { setError(err.message || 'Failed to load clients'); } } f(); }, []);
  useEffect(() => {
    if (project) {
      setEditName(project.name); setEditCode(project.code || ''); setEditStatus(project.status);
      setEditClientId(project.clientId || ''); setEditBillingMethod(project.billingMethod || 'PROJECT');
      setEditStartDate(project.startDate ? project.startDate.slice(0, 10) : '');
      setEditEndDate(project.endDate ? project.endDate.slice(0, 10) : '');
    }
  }, [project]);

  useEffect(() => {
    if (!id || !isAdmin) return;
    async function f() {
      setPermLoading(true);
      try {
        const d = await api<any[]>(`/permissions/admin/projects/${id}/permissions`);
        setProjectPermissions(d || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load permissions');
      } finally {
        setPermLoading(false);
      }
    }
    f();
  }, [id, isAdmin]);

  async function doEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setBusy(true);
    try {
      await api(`/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: editName, code: editCode, status: editStatus, clientId: editClientId, billingMethod: editBillingMethod, startDate: editStartDate || undefined, endDate: editEndDate || undefined }),
      });
      setEditOpen(false); await fetchProject();
    } catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function createTask(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !taskName.trim()) return;
    setTaskBusy(true); setError('');
    try {
      await api(`/projects/${id}/tasks`, { method: 'POST', body: JSON.stringify({ name: taskName.trim(), billable: taskBillable }) });
      setTaskName(''); setTaskBillable(true);
      const data = await api<Task[]>(`/projects/${id}/tasks`); setTasks(data || []);
    } catch (err: any) { setError(err.message || 'Failed to add task'); } finally { setTaskBusy(false); }
  }

  async function deactivateTask(taskId: string) {
    if (!id) return;
    setError('');
    try {
      await api(`/projects/${id}/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify({ active: false }) });
      const data = await api<Task[]>(`/projects/${id}/tasks`); setTasks(data || []);
    } catch (err: any) { setError(err.message || 'Failed to deactivate task'); }
  }

  async function addMember(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    if (!memberUserId) { setError('Please select a user to add'); return; }
    setMemberBusy(true); setError('');
    try {
      await api(`/projects/${id}/members`, { method: 'POST', body: JSON.stringify({ userId: memberUserId, role: memberRole }) });
      setMemberUserId(''); await fetchProject();
    } catch (err: any) { setError(err.message || 'Failed to add member'); } finally { setMemberBusy(false); }
  }

  async function removeMember(userId: string) {
    if (!id) return;
    setError('');
    try { await api(`/projects/${id}/members/${userId}`, { method: 'DELETE' }); await fetchProject(); } catch (err: any) { setError(err.message || 'Failed to remove member'); }
  }

  function getProjectPerm(role: string, pageKey: string): string {
    const found = projectPermissions.find((p: any) => p.role === role && p.pageKey === pageKey);
    return found ? found.accessLevel : 'NONE';
  }

  function setProjectPerm(role: string, pageKey: string, accessLevel: string) {
    setProjectPermissions(prev => {
      const idx = prev.findIndex((p: any) => p.role === role && p.pageKey === pageKey);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], accessLevel };
        return next;
      }
      return [...prev, { role, projectId: id!, pageKey, accessLevel }];
    });
    setModifiedPerms(prev => new Set(prev).add(`${role}:${pageKey}`));
  }

  async function saveProjectPermissions() {
    if (!id) return;
    setPermSaving(true); setError('');
    try {
      const permissions = ROLES.flatMap(role =>
        PAGE_KEYS
          .filter(pageKey => modifiedPerms.has(`${role}:${pageKey}`))
          .map(pageKey => ({
            role,
            pageKey,
            accessLevel: getProjectPerm(role, pageKey),
          }))
      );
      await api(`/permissions/admin/projects/${id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions }),
      });
      setModifiedPerms(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to save permissions');
    } finally {
      setPermSaving(false);
    }
  }

  const taskColumns = [
    { key: 'name', header: 'Name' },
    { key: 'billable', header: 'Billable', render: (t: Task) => <Badge variant={t.billable ? 'success' : 'secondary'}>{t.billable ? 'Yes' : 'No'}</Badge> },
    { key: 'rate', header: 'Rate', render: (t: Task) => t.rate != null ? `CHF ${Number(t.rate).toFixed(2)}` : '-' },
    { key: 'actions', header: '', className: 'w-10', render: (t: Task) => canWrite ? <Button data-testid="delete-task-button" variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-muted-foreground)] hover:text-destructive" onClick={() => deactivateTask(t.id)}><Trash2 className="h-4 w-4" /></Button> : null },
  ];

  const memberColumns = [
    { key: 'avatar', header: '', render: (m: any) => <Avatar className="h-8 w-8"><AvatarFallback>{(m.user.name || '?').charAt(0).toUpperCase()}</AvatarFallback></Avatar> },
    { key: 'name', header: 'Name', render: (m: any) => m.user.name },
    { key: 'role', header: 'Role', render: (m: any) => <Badge variant={m.role === 'PM' ? 'default' : 'secondary'}>{m.role}</Badge> },
    { key: 'actions', header: '', className: 'w-10', render: (m: any) => canWrite ? <Button data-testid="remove-member-button" variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-muted-foreground)] hover:text-destructive" onClick={() => removeMember(m.user.id)}><Trash2 className="h-4 w-4" /></Button> : null },
  ];

  if (loading) return <div className="animate-in space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-40 w-full" /><Skeleton className="h-40 w-full" /></div>;
  if (!project) return <div className="animate-in"><EmptyState icon={FolderKanban} title="Project not found" description="The project does not exist or you do not have access." action={<Button onClick={() => navigate('/projects')}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>} /></div>;

  const memberships = (project as any).memberships || [];

  return (
    <div className="animate-in space-y-6">
      <PageHeader
        title={project.name}
        description={
          <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <Badge variant={project.status === 'ACTIVE' ? 'success' : project.status === 'QUEUED' ? 'warning' : 'secondary'}>{project.status}</Badge>
            <span>&middot; {project.client?.name || 'No client'}</span>
            <span>&middot; <span className="font-mono text-xs">{project.code}</span></span>
          </div>
        }
      >
        <div className="flex items-center gap-2">
          {canWrite && (<Button data-testid="edit-project-button" variant="outline" size="sm" onClick={() => setEditOpen(true)}><Pencil className="mr-2 h-4 w-4" /> Edit</Button>)}
          <Button variant="ghost" size="sm" onClick={() => navigate('/projects')}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
        </div>
      </PageHeader>
      {error && <Badge variant="destructive">{error}</Badge>}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview"><FolderKanban className="mr-1 h-4 w-4" /> Overview</TabsTrigger>
          <TabsTrigger value="tasks"><ListChecks className="mr-1 h-4 w-4" /> Tasks</TabsTrigger>
          <TabsTrigger value="members"><Users className="mr-1 h-4 w-4" /> Members</TabsTrigger>
          <TabsTrigger value="budgets"><Wallet className="mr-1 h-4 w-4" /> Budgets</TabsTrigger>
          {isAdmin && <TabsTrigger data-testid="project-permissions-tab" value="permissions"><ShieldCheck className="mr-1 h-4 w-4" /> Permissions</TabsTrigger>}
        </TabsList>
        <TabsContent value="overview" className="space-y-4 pt-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Status</CardTitle></CardHeader><CardContent><Badge variant={project.status === 'ACTIVE' ? 'success' : project.status === 'QUEUED' ? 'warning' : 'secondary'}>{project.status}</Badge></CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Billing Method</CardTitle></CardHeader><CardContent className="text-sm font-semibold">{project.billingMethod}</CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Alert Threshold</CardTitle></CardHeader><CardContent className="text-sm font-semibold">{project.alertThreshold != null ? `${project.alertThreshold}%` : 'None'}</CardContent></Card>
          </div>
          {project.startDate || project.endDate ? (
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Dates</CardTitle></CardHeader><CardContent className="flex items-center gap-2 text-sm"><CalendarDays className="h-4 w-4 text-[var(--color-muted-foreground)]" /><span>{project.startDate ? new Date(project.startDate).toLocaleDateString() : '—'} &mdash; {project.endDate ? new Date(project.endDate).toLocaleDateString() : '—'}</span></CardContent></Card>
          ) : null}
        </TabsContent>
        <TabsContent value="tasks" className="pt-4 space-y-4">
          {canWrite && (
            <form onSubmit={createTask} className="flex flex-wrap items-end gap-3">
              <div className="w-full max-w-sm space-y-2"><Label>New task</Label><Input value={taskName} onChange={e => setTaskName(e.target.value)} placeholder="e.g. Design, Development" required /></div>
              <div className="flex items-center gap-2 pb-2.5"><input id="td-billable" type="checkbox" className="h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]" checked={taskBillable} onChange={e => setTaskBillable(e.target.checked)} /><Label htmlFor="td-billable" className="text-sm font-normal">Billable</Label></div>
              <Button type="submit" loading={taskBusy} disabled={taskBusy}><Plus className="mr-2 h-4 w-4" /> Add Task</Button>
            </form>
          )}
          <DataTable columns={taskColumns} data={tasks} keyExtractor={t => t.id} mobileRender={(t: Task) => (
            <div className="flex items-center justify-between"><div><div className="text-sm font-medium">{t.name}</div><div className="text-xs text-[var(--color-muted-foreground)]">{t.billable ? 'Billable' : 'Non-billable'} &middot; {t.rate != null ? `CHF ${t.rate}` : '-'}</div></div>{canWrite && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deactivateTask(t.id)}><Trash2 className="h-4 w-4" /></Button>}</div>
          )} emptyState={<EmptyState icon={ListChecks} title="No tasks" description="Add tasks to track time against this project." />} />
        </TabsContent>
        <TabsContent value="members" className="pt-4 space-y-4">
          {canWrite && (
            <form onSubmit={addMember} className="flex flex-wrap items-end gap-3">
              <div className="w-full max-w-xs space-y-2"><Label>User</Label><Select value={memberUserId} onValueChange={setMemberUserId} required><SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger><SelectContent>{usersList.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="w-full max-w-xs space-y-2"><Label>Role</Label><Select value={memberRole} onValueChange={(v) => setMemberRole(v as 'MEMBER' | 'PM')}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="MEMBER">Member</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent></Select></div>
              <Button type="submit" loading={memberBusy} disabled={memberBusy || !memberUserId || usersList.length === 0}><Plus className="mr-2 h-4 w-4" /> Add Member</Button>
            </form>
          )}
          <DataTable columns={memberColumns} data={memberships.map((m: any) => ({ user: m.user, role: m.role }))} keyExtractor={(m: any) => m.user.id} mobileRender={(m: any) => (
            <div className="flex items-center gap-3"><Avatar className="h-8 w-8"><AvatarFallback>{(m.user.name || '?').charAt(0).toUpperCase()}</AvatarFallback></Avatar><div className="flex-1 min-w-0"><div className="text-sm font-medium">{m.user.name}</div><div className="text-xs text-[var(--color-muted-foreground)]">{m.role}</div></div>{canWrite && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeMember(m.user.id)}><Trash2 className="h-4 w-4" /></Button>}</div>
          )} emptyState={<EmptyState icon={Users} title="No members" description="Assign users to this project." />} />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="permissions" className="pt-4 space-y-4">
            {permLoading ? (
              <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
            ) : (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Lock className="h-4 w-4 text-[var(--color-primary)]" /> Project Permissions</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Role</th>
                          {PAGE_KEYS.map(pk => (
                            <th key={pk} className="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">{pk}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ROLES.map(role => (
                          <tr key={role}>
                            <td className="px-2 py-2 font-medium whitespace-nowrap">{role}</td>
                            {PAGE_KEYS.map(pageKey => (
                              <td key={`${role}-${pageKey}`} className="px-2 py-1">
                                <Select value={getProjectPerm(role, pageKey)} onValueChange={(val) => setProjectPerm(role, pageKey, val)}>
                                  <SelectTrigger className="h-8 min-w-[7rem] text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="NONE">NONE</SelectItem>
                                    <SelectItem value="VIEWOWN">VIEWOWN</SelectItem>
                                    <SelectItem value="READ">READ</SelectItem>
                                    <SelectItem value="READWRITE">READWRITE</SelectItem>
                                    <SelectItem value="ADMIN">ADMIN</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button data-testid="save-project-permissions-button" loading={permSaving} disabled={permSaving} onClick={saveProjectPermissions}>Save Changes</Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
        <TabsContent value="budgets" className="pt-4 space-y-4"><ProjectBudgets projectId={id!} /></TabsContent>
      </Tabs>
      {canWrite && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Project</DialogTitle><DialogDescription>Update project details.</DialogDescription></DialogHeader>
            <form id="edit-project-form" onSubmit={doEdit} className="space-y-4 py-2">
              <div className="space-y-2"><Label>Name</Label><Input value={editName} onChange={e => setEditName(e.target.value)} required /></div>
              <div className="space-y-2"><Label>Code</Label><Input value={editCode} onChange={e => setEditCode(e.target.value)} required /></div>
              <div className="space-y-2"><Label>Client</Label><Select value={editClientId} onValueChange={setEditClientId}><SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger><SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Status</Label><Select value={editStatus} onValueChange={setEditStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ACTIVE">Active</SelectItem><SelectItem value="CLOSED">Closed</SelectItem><SelectItem value="ARCHIVED">Archived</SelectItem><SelectItem value="QUEUED">Queued</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Billing Method</Label><Select value={editBillingMethod} onValueChange={setEditBillingMethod}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="PROJECT">Project</SelectItem><SelectItem value="PERSON">Person</SelectItem><SelectItem value="TASK">Task</SelectItem></SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label>Start Date</Label><Input type="date" value={editStartDate} onChange={e => setEditStartDate(e.target.value)} /></div><div className="space-y-2"><Label>End Date</Label><Input type="date" value={editEndDate} onChange={e => setEditEndDate(e.target.value)} /></div></div>
            </form>
            <DialogFooter><Button type="submit" form="edit-project-form" loading={busy} disabled={busy}>Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ProjectBudgets({ projectId }: { projectId: string }) {
  const [budgets, setBudgets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    async function f() { try { const d = await api(`/budgets?scope=PROJECT&scopeId=${projectId}`) as any[]; setBudgets(d || []); } catch {} setLoading(false); }
    f();
  }, [projectId]);
  if (loading) return <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div>;
  if (budgets.length === 0) return <EmptyState icon={Wallet} title="No budgets" description="Add budgets from the Budgets page to see them here." />;
  const maxHours = Math.max(1, ...budgets.map((b: any) => Number(b.hours)));
  return (
    <div className="space-y-3">
      {budgets.map((b: any) => (
        <div key={b.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
          <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold">{b.level}</span><span className="font-mono text-xs">{Number(b.hours).toFixed(1)}h</span></div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--color-slate-100)]"><div className="h-full rounded-full bg-[var(--color-primary)] transition-all" style={{ width: `${Math.min(100, (Number(b.hours) / maxHours) * 100)}%` }} /></div>
          <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{b.period || 'Total'} {b.amountChf != null && `\u00b7 CHF ${Number(b.amountChf).toFixed(2)}`}</div>
        </div>
      ))}
    </div>
  );
}
